import { useState } from "react";
import { Link } from "react-router";
import { SourceTable } from "../components/SourceTable";
import { TrendChart } from "../components/TrendChart";
import {
  Alert,
  Card,
  CardHeader,
  EmptyState,
  LoadingBlock,
  PageHeader,
  Stat,
  StatGroup,
  Table,
  Tag,
  Td,
  Th,
} from "../components/ui";
import { WindowPicker } from "./LinkPage";
import { AFFILIATE_SERIES, formatEpc, formatRate, linkMetrics } from "../lib/insights";
import { formatCount, formatMoney } from "../lib/money";
import { useAffiliateTrend, usePerformance, useSources } from "../lib/queries";
import { useTranslation } from "react-i18next";

/**
 * Where the traffic comes from, and which campaign is worth more of it.
 *
 * The screen an affiliate opens to decide where to spend the next hour. It
 * answers that in two halves, and they are separate because the data can only
 * honestly join them at the link:
 *
 * - **Campaigns**, ranked by what a click was worth. That folds the entire
 *   funnel into one comparable number, which total earnings does not — total
 *   earnings rewards whichever link has been up longest.
 * - **Sources**, which says where the clicks arrived from. It cannot say which
 *   of them converted: a signup reaches this programme with a code and no
 *   visitor identity, so there is nothing to join a conversion back to a
 *   referrer host on.
 *
 * The bridge between the two is the affiliate's own habit of making a link per
 * placement, and the empty state says so rather than leaving them to work it
 * out from a table that quietly cannot answer their question.
 */
export function TrafficPage() {
  const { t } = useTranslation("traffic");
  const [days, setDays] = useState(30);
  const sources = useSources(days);
  const trend = useAffiliateTrend(days);
  const campaigns = usePerformance(days);

  const points = trend.data ?? [];
  const windowed = points.reduce(
    (sum, point) => ({
      clicks: sum.clicks + point.clicks,
      signups: sum.signups + point.signups,
      conversions: sum.conversions + point.conversions,
      earnedMicros: sum.earnedMicros + point.earnedMicros,
    }),
    { clicks: 0, signups: 0, conversions: 0, earnedMicros: 0 },
  );
  const windowMetrics = linkMetrics({
    clicks: windowed.clicks,
    referrals: windowed.signups,
    conversions: windowed.conversions,
    earnedMicros: windowed.earnedMicros,
  });

  /*
   * Ranked by earnings per click, which is the comparison this page exists to
   * make. Links with no clicks sort last rather than first — an undefined rate
   * is not a good one.
   *
   * These counts are *windowed*, unlike the ones on the links list. Ranking on
   * lifetime totals under a control that says "last 30 days" would put a
   * campaign that earned well last spring above one working now, on the screen
   * whose entire job is deciding where to spend the next hour.
   */
  const ranked = [...(campaigns.data ?? [])]
    .map((link) => ({ link, metrics: linkMetrics({ ...link, referrals: link.signups }) }))
    .sort((a, b) => {
      const left = Number.isFinite(a.metrics.epcMicros) ? a.metrics.epcMicros : -1;
      const right = Number.isFinite(b.metrics.epcMicros) ? b.metrics.epcMicros : -1;
      return right - left;
    });

  return (
    <>
      <PageHeader
        title={t("title")}
        description={t("description")}
        action={<WindowPicker days={days} onDays={setDays} />}
      />

      <StatGroup>
        <Stat
          boxed={false}
          size="lg"
          label={t("clicks")}
          value={formatCount(windowed.clicks)}
          hint={t("inLastDays", { count: days })}
          tone={windowed.clicks === 0 ? "muted" : "default"}
        />
        <Stat
          boxed={false}
          size="lg"
          label={t("signedUp")}
          value={formatCount(windowed.signups)}
          hint={t("ofClicks", { rate: formatRate(windowMetrics.signupRate) })}
          tone={windowed.signups === 0 ? "muted" : "default"}
        />
        <Stat
          boxed={false}
          size="lg"
          label={t("becameCustomers")}
          value={formatCount(windowed.conversions)}
          hint={t("ofSignups", { rate: formatRate(windowMetrics.conversionRate) })}
          tone={windowed.conversions === 0 ? "muted" : "default"}
        />
        <Stat
          boxed={false}
          size="lg"
          label={t("perClick")}
          value={formatEpc(windowMetrics.epcMicros)}
          hint={t("earned", { amount: formatMoney(windowed.earnedMicros) })}
          tone={windowed.earnedMicros === 0 ? "muted" : "default"}
        />
      </StatGroup>

      <Card>
        <CardHeader
          title={t("campaigns.title")}
          description={t("campaigns.description", { count: days })}
        />
        {campaigns.isPending ? (
          <LoadingBlock />
        ) : campaigns.error ? (
          <div className="px-4 py-4">
            <Alert>{campaigns.error.message}</Alert>
          </div>
        ) : ranked.length === 0 ? (
          <EmptyState
            title={t("campaigns.empty")}
            description={t("campaigns.emptyDescription")}
          />
        ) : (
          <Table
            head={
              <tr>
                <Th>{t("th.campaign")}</Th>
                <Th className="text-right">{t("th.clicks")}</Th>
                <Th priority="lg" className="text-right">
                  {t("th.signupRate")}
                </Th>
                <Th priority="md" className="text-right">
                  {t("th.customers")}
                </Th>
                <Th className="text-right">{t("th.perClick")}</Th>
              </tr>
            }
          >
            {ranked.map(({ link, metrics }) => (
              <tr key={link.linkId}>
                <Td>
                  <Link
                    className="inline-flex flex-wrap items-center gap-2 text-sm text-fg transition-colors duration-200 ease-smooth hover:text-accent"
                    to={`/links/${link.linkId}`}
                  >
                    <Tag>{link.code}</Tag>
                    {link.label && <span className="text-fg-muted">{link.label}</span>}
                    {/* Archived campaigns stay in the ranking — somebody
                        comparing last quarter's placements wants the one they
                        have since retired in the comparison — so the row says
                        so rather than the list quietly omitting it. */}
                    {link.archived && (
                      <span className="text-[11px] text-fg-subtle">{t("archived")}</span>
                    )}
                  </Link>
                </Td>
                <Td className="text-right tabular-nums">{formatCount(link.clicks)}</Td>
                <Td priority="lg" className="text-right tabular-nums text-fg-muted">
                  {formatRate(metrics.signupRate)}
                </Td>
                <Td priority="md" className="text-right tabular-nums">
                  {formatCount(link.conversions)}
                </Td>
                {/*
                 * The ranking column, and the earned total beside it — because
                 * "$2.10 per click" from four clicks and from four hundred are
                 * different facts, and a rate with no volume behind it is how
                 * somebody talks themselves into a campaign that earned $8.
                 */}
                <Td className="text-right tabular-nums whitespace-nowrap">
                  {formatEpc(metrics.epcMicros)}
                  <span className="text-fg-subtle"> · {formatMoney(link.earnedMicros)}</span>
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Card>
        <CardHeader
          title={t("sourcesTitle")}
          description={t("sourcesDescription")}
        />
        {sources.isPending ? (
          <LoadingBlock />
        ) : sources.error ? (
          <div className="px-4 py-4">
            <Alert>{sources.error.message}</Alert>
          </div>
        ) : (
          <SourceTable sources={sources.data} showLink />
        )}
      </Card>

      <Card>
        <CardHeader title={t("overTime")} description={t("overTimeDescription", { count: days })} />
        {trend.isPending ? (
          <LoadingBlock />
        ) : trend.error ? (
          <div className="px-4 py-4">
            <Alert>{trend.error.message}</Alert>
          </div>
        ) : (
          <TrendChart points={trend.data} series={AFFILIATE_SERIES} />
        )}
      </Card>
    </>
  );
}
